import { Component, Input, } from '@angular/core';
import { PocketbaseService } from '../../services/pocketbase.service';
import { Course } from '../../interfaces/courseInterface';
import { Router, RouterLink } from '@angular/router';
import { TestService } from '../../services/test.service';
import { CommonModule } from '@angular/common';
import { NgIconComponent } from '@ng-icons/core';
import { AchievementService } from '../../services/achievement.service';
import { Achievement } from '../../interfaces/achievementInterface';
import { lastValueFrom, take, timer } from 'rxjs';
import { ExplainerTextComponent } from '../../components/explainer-text/explainer-text.component';
import { CourseBackgroundImageService } from '../../services/course-background-image.service';

@Component({
    selector: 'app-training-overview',
    imports: [RouterLink, NgIconComponent, CommonModule, ExplainerTextComponent],
    templateUrl: './training-overview.component.html',
    styleUrl: './training-overview.component.scss'
})
export class TrainingOverviewComponent {
  public availableCourses: {
    id: string,
    examId: string,
    name: string,
    access: boolean,
    hasBookAccess: boolean,
    bookDownloadUrl?: string,
    bookCoverUrl?: string,
    price: number
  }[] = [];
  public lastResults: { date: Date, results: { id: string, title: string, score: number, timeTaken: number, questionAmount: number, startTime: Date }[] }[] = [];
  public achievements: Achievement[] = [];
  public isLoading = false;

  @Input()
  public selectedCourseId: string = 'all';

  constructor(private pocketbaseService: PocketbaseService, private testService: TestService, private achievementService: AchievementService, private router: Router, private courseBackgroundImageService: CourseBackgroundImageService) { }

  ngOnInit(): void {
    this.fetchCourses();
    this.fetchAchievements();
  }

  async fetchCourses() {
    this.availableCourses = [];

    const courses = await this.pocketbaseService.pb.collection('courses').getFullList<Course>({
      expand: 'exam'
    });

    const paidBookPurchasesByCourse = new Set<string>();
    const authUserId = this.pocketbaseService.pb.authStore.model?.['id'] as string | undefined;
    if (authUserId) {
      try {
        const paidBookPurchases = await this.pocketbaseService.pb.collection('purchases').getFullList<{
          course: string,
          productType?: 'book' | 'bundle' | 'course',
          product?: 'book' | 'bundle' | 'course'
        }>({
          filter: `user = "${authUserId}" && status = "paid"`,
          fields: 'course,productType,product'
        });

        paidBookPurchases.forEach((purchase) => {
          const purchaseKind = purchase.productType || purchase.product;
          if (purchaseKind === 'book' || purchaseKind === 'bundle') {
            paidBookPurchasesByCourse.add(purchase.course);
          }
        });
      } catch (error) {
        console.warn('Could not load paid book purchases for training overview', error);
      }
    }

    const filteredAndSortedCourses = courses
      .filter(course => course.expand?.exam)
      .filter(course => !(course.isDemoCourse && !course.access))
      .sort((a, b) => {
        if (a.access === b.access) return 0; // dont change order if they have the same access
        return a.access ? -1 : 1; // otherwise, the one that has access goes first
      });

    for (const course of filteredAndSortedCourses) {
      const hasBookAccess = paidBookPurchasesByCourse.has(course.id);
      const bookFileName = this.extractFileName(course.bookPdf);
      const bookCoverFileName = this.extractFileName(course.bookCover);

      const bookDownloadUrl = hasBookAccess && bookFileName
        ? await this.pocketbaseService.getCourseFileUrl(course.id, bookFileName, true)
        : undefined;
      const bookCoverUrl = hasBookAccess && bookCoverFileName
        ? await this.pocketbaseService.getCourseFileUrl(course.id, bookCoverFileName)
        : undefined;

      this.availableCourses.push({
        id: course.id,
        examId: course.exam,
        name: course.name,
        access: course.access,
        hasBookAccess,
        price: course.price,
        bookDownloadUrl,
        bookCoverUrl
      });
    }
  }

  async fetchAchievements() {
    this.achievements = await this.achievementService.getAchievements();
  }

  selectCourse(courseId: string) {
    this.isLoading = true;
    lastValueFrom(timer(125).pipe(take(1))).then(() => this.isLoading = false);
    this.router.navigate(['/training', courseId]);
  }

  public getCourseCardBackground(courseName: string): string {
    return this.courseBackgroundImageService.getCourseCardBackground(courseName);
  }

  private extractFileName(file?: string | string[]): string | undefined {
    if (Array.isArray(file)) {
      return file[0];
    }

    return file;
  }

}
